import i18n from "i18next";

/**
 * Longest-prefix match → namespaces to warm before navigation.
 * Unmatched paths no-op; the page still loads via useTranslation.
 */
const ROUTE_NAMESPACE_PREFIXES = [
  { prefix: "/login", namespaces: ["login"] },
  { prefix: "/auth", namespaces: ["login"] },
  { prefix: "/home", namespaces: ["home"] },
  { prefix: "/my-work", namespaces: ["myWork", "operationalExecution"] },
  { prefix: "/ticketing/settings", namespaces: ["ticketingSettings", "ticketingAdmin"] },
  { prefix: "/ticketing", namespaces: ["ticketingList", "ticketingSettings"] },
  { prefix: "/itsm", namespaces: ["itsmProblem", "itsmChangeRequest"] },
  { prefix: "/incident-management", namespaces: ["incidentIncidents"] },
  { prefix: "/risk-management/risk-register", namespaces: ["riskRegister", "assets"] },
  { prefix: "/risk-management", namespaces: ["riskMitigation", "riskThreat", "riskRegister"] },
  { prefix: "/asset-inventory-management", namespaces: ["assetInventoryAssets", "assets"] },
  { prefix: "/bcm/bia", namespaces: ["bcmBia", "bcmCommon", "lifecycle"] },
  { prefix: "/bcm/bcp", namespaces: ["bcmBcp", "bcmCommon", "bcmBia", "assets", "vendors", "lifecycle", "teamAssociation"] },
  { prefix: "/bcm/crisis-event", namespaces: ["bcmCrisisEvent", "bcmCommon", "teamAssociation", "bcmBia", "bcmBcp", "vendors", "assets", "lifecycle", "operationalExecution"] },
  { prefix: "/bcm/exercises", namespaces: ["bcmExercise", "bcmCommon", "teamAssociation", "operationalExecution", "bcmCrisisEvent", "internalAuditAssessment"] },
  { prefix: "/bcm", namespaces: ["bcmCommon"] },
  { prefix: "/gdpr-operations/obtaining-explicit-consent", namespaces: ["gdprObtainingExplicitConsent"] },
  { prefix: "/gdpr-operations/dpa-registry", namespaces: ["gdprDpa"] },
  { prefix: "/gdpr-operations/privacy-notice", namespaces: ["gdprPrivacyNotice"] },
  { prefix: "/gdpr-operations/consent-record", namespaces: ["gdprConsentRecord"] },
  { prefix: "/gdpr-operations/data-subject-request", namespaces: ["gdprDataSubjectRequest"] },
  { prefix: "/gdpr-operations/dpia-assessment", namespaces: ["gdprDpiaAssessment"] },
  { prefix: "/gdpr-operations", namespaces: ["gdprApplicationTracking", "gdprDashboard"] },
  { prefix: "/iso-27001", namespaces: ["iso27001Objective", "iso27001MyTraining"] },
  { prefix: "/isms-controls", namespaces: ["ismsControlsVulnerability"] },
  { prefix: "/vendor-management", namespaces: ["vendors"] },
  { prefix: "/report-operations", namespaces: ["reportOperationsDocument", "reportOperationsReports"] },
  { prefix: "/reports", namespaces: ["reportStudio", "reportArchive", "reportScheduling", "reportSurface"] },
  { prefix: "/training", namespaces: ["iso27001Training", "iso27001MyTraining", "iso27001Competence"] },
  { prefix: "/public-docs", namespaces: ["publicDocuments"] },
  { prefix: "/public/dsr", namespaces: ["gdprPublicDsr"] },
  { prefix: "/settings", namespaces: ["notificationPreferences", "communicationProvider"] },
  { prefix: "/bigdes-operations", namespaces: ["bigdesCurrentSituationEvaluation", "bigdesAuditReports"] },
  { prefix: "/definitions/organization/duty-definitions", namespaces: ["teamAssociation"] },
  { prefix: "/definitions/organization/team", namespaces: ["generalSystemTeam", "teamAssociation"] },
  { prefix: "/definitions/organization/process", namespaces: ["generalSystemProcess"] },
  { prefix: "/definitions/organization/units", namespaces: ["generalSystemUnit"] },
  { prefix: "/definitions/organization/root-cause", namespaces: ["generalSystemRootCause"] },
  { prefix: "/definitions/organization/mail-template", namespaces: ["generalSystemMailTemplate"] },
  { prefix: "/definitions/survey-responses", namespaces: ["definitionsSurveyResponses"] },
  { prefix: "/definitions/surveys", namespaces: ["definitionsSurveys"] },
  { prefix: "/definitions/scale/bia-impact-scale", namespaces: ["biaImpactConfiguration"] },
  { prefix: "/definitions/scale", namespaces: ["matrixConfiguration"] },
  { prefix: "/definitions/report-branding", namespaces: ["reportBranding"] },
  { prefix: "/definitions/compliance", namespaces: ["definitionsNav"] },
  { prefix: "/definitions/privacy", namespaces: ["definitionsNav"] },
  { prefix: "/definitions/kvkk/company", namespaces: ["kvkkCompany"] },
  { prefix: "/definitions/kvkk/category", namespaces: ["kvkkCategory"] },
  { prefix: "/definitions/kvkk/processing-purpose", namespaces: ["kvkkProcessingPurpose"] },
  { prefix: "/definitions/kvkk/subject-person-group", namespaces: ["kvkkSubjectPersonGroup"] },
  { prefix: "/definitions/kvkk/collection-method", namespaces: ["kvkkCollectionMethod"] },
  { prefix: "/definitions/kvkk/legal-reason", namespaces: ["kvkkLegalReason"] },
  { prefix: "/definitions/kvkk/international-transfer/transfer-types", namespaces: ["kvkkTransferType"] },
  { prefix: "/definitions/kvkk/international-transfer", namespaces: ["kvkkInternationalTransfer"] },
  { prefix: "/definitions/kvkk/recipient-group", namespaces: ["kvkkSimpleDefinitions"] },
  { prefix: "/definitions/kvkk/data-disposal-method", namespaces: ["kvkkSimpleDefinitions"] },
  { prefix: "/definitions/kvkk/data-source", namespaces: ["kvkkSimpleDefinitions"] },
  { prefix: "/definitions/kvkk/privacy-principle", namespaces: ["kvkkSimpleDefinitions"] },
  { prefix: "/definitions/kvkk/security-measure", namespaces: ["kvkkSimpleDefinitions"] },
  { prefix: "/definitions/obtaining-explicit-consent/method", namespaces: ["obtainingExplicitConsentMethod"] },
  { prefix: "/definitions/obtaining-explicit-consent/reason", namespaces: ["obtainingExplicitConsentReason"] },
  { prefix: "/definitions/application-tracking/application-method", namespaces: ["applicationTrackingMethod"] },
  { prefix: "/definitions/application-tracking/application-request-information", namespaces: ["applicationTrackingRequestInformation"] },
  { prefix: "/definitions/document/document-type", namespaces: ["documentType"] },
  { prefix: "/definitions/document/classification", namespaces: ["classification"] },
  { prefix: "/definitions/integrations/connectors", namespaces: ["thirdPartyApplications"] },
  { prefix: "/definitions/integrations/tenant-directory", namespaces: ["definitionsNav"] },
  { prefix: "/definitions/integrations/identity-providers", namespaces: ["definitionsNav"] },
  { prefix: "/user-operations", namespaces: ["userOperationsRoleManagement"] },
  { prefix: "/platform/workflow-templates", namespaces: ["workflow"] },
  { prefix: "/platform/communications/notification-policies", namespaces: ["notificationPolicy"] },
  { prefix: "/platform/communications/templates", namespaces: ["notificationTemplate"] },
  { prefix: "/platform/communications/mfa", namespaces: ["platformMfa"] },
  { prefix: "/platform/communications/delivery-history", namespaces: ["notificationPolicy"] },
  { prefix: "/platform/third-party-applications", namespaces: ["thirdPartyApplications"] },
  { prefix: "/platform/scheduled-report-distribution", namespaces: ["reportScheduling"] },
  { prefix: "/platform/party-templates", namespaces: ["party"] },
  { prefix: "/platform/assignment-templates", namespaces: ["assignmentTemplates"] },
  { prefix: "/platform/business-calendar", namespaces: ["businessCalendar"] },
  { prefix: "/platform/seed-policy", namespaces: ["seedPolicy"] },
  { prefix: "/platform/tailorability", namespaces: ["tailorability"] },
  { prefix: "/admin/webhooks", namespaces: ["admin"] },
  { prefix: "/admin/queues", namespaces: ["userOperationsQueueMonitor"] },
  { prefix: "/admin", namespaces: ["admin"] },
  { prefix: "/context-management", namespaces: ["organizationalContext"] },
  { prefix: "/audit-compliance", namespaces: ["auditComplianceKpiDashboard"] },
  { prefix: "/internal-control", namespaces: ["internalAuditAssessment"] },
  { prefix: "/setup-roadmap", namespaces: ["setupGuide"] },
  { prefix: "/setup", namespaces: ["setup"] },
  { prefix: "/user-manual/design-system", namespaces: ["designSystem"] },
  { prefix: "/user-manual/about-sgrc", namespaces: ["aboutSgrc"] },
  { prefix: "/user-manual", namespaces: ["patchNotes"] },
  { prefix: "/dashboard", namespaces: ["dashboard"] },
];

/** Sorted longest-prefix-first for stable matching. */
const SORTED_PREFIXES = [...ROUTE_NAMESPACE_PREFIXES].sort(
  (a, b) => b.prefix.length - a.prefix.length,
);

/**
 * @param {string} pathname
 * @returns {string[]}
 */
export function resolveNamespacesForPath(pathname) {
  if (!pathname || typeof pathname !== "string") return [];
  const path = pathname.split("?")[0].split("#")[0] || "/";
  const match = SORTED_PREFIXES.find(
    (entry) => path === entry.prefix || path.startsWith(`${entry.prefix}/`),
  );
  return match ? [...match.namespaces] : [];
}

const prefetchInFlight = new Set();

/**
 * Fire-and-forget namespace warm for a target URL (hover / click).
 * @param {string} url
 * @returns {Promise<void>}
 */
export function prefetchNamespacesForPath(url) {
  if (!url || url === "#") return Promise.resolve();
  let pathname = url;
  try {
    pathname = url.startsWith("http") ? new URL(url).pathname : url.split("?")[0];
  } catch {
    pathname = String(url).split("?")[0];
  }

  const namespaces = resolveNamespacesForPath(pathname).filter(
    (ns) => !i18n.hasResourceBundle(i18n.language, ns) && !prefetchInFlight.has(ns),
  );
  if (namespaces.length === 0) return Promise.resolve();

  for (const ns of namespaces) prefetchInFlight.add(ns);
  return i18n
    .loadNamespaces(namespaces)
    .catch(() => {})
    .finally(() => {
      for (const ns of namespaces) prefetchInFlight.delete(ns);
    });
}

export { ROUTE_NAMESPACE_PREFIXES };
